/**
 * Scenario 10 — Cascade (docs/SCENARIOS.md).
 *
 * Two dams, one river. The engine runs **both sites**: Kettle Narrows steps first, and its gauge
 * flow is injected into Lower Sable's upstream queue, arriving 14 hours later.
 *
 * The downstream scenario's weather is deliberately **weak**. Lower Sable's site config describes a
 * 62,000 km² basin, which already contains Kettle Narrows' 21,000 km². Authoring the downstream
 * weather at full strength and *then* adding the upstream release would count the headwater
 * catchment twice. The keyframes below are calibrated so Lower Sable's own hydrology supplies only
 * the local contribution, and the upstream release makes up the rest — checked in
 * `tests/cascade.test.ts`, which asserts the combined river is inside its expected band and that
 * the downstream plant sees a delayed, attenuated copy of the upstream release.
 */

import type { ScenarioConfig } from '../engine/types';

const downstream: ScenarioConfig = {
  id: 's10-cascade-downstream',
  index: 10,
  name: 'Cascade — Lower Sable',
  siteId: 'lower-sable',
  start: '2026-05-28T00:00',
  durationH: 168,
  teaches: 'Receiving another operator’s water.',
  briefing:
    'You are the downstream plant. Your inflow is somebody else’s decision, arriving fourteen hours ' +
    'after they make it, and you have a metre and a half of pondage to absorb it in.\n\n' +
    'Watch the upstream release, not your own forebay: by the time the forebay moves, the water ' +
    'that moved it left Kettle Narrows half a day ago.',
  initial: {
    headwater: 117.6,
    swe: [0, 240, 560],
    baseflow: 900,
    wetness: 0.45,
    unitsOnline: [0, 1, 2, 3],
    initialSetpointMW: 80,
  },
  // Local basin only — the upstream release supplies the rest.
  weather: [
    { h: 0, tempC: 6, precipMmH: 0.03 },
    { h: 84, tempC: 7, precipMmH: 0.03 },
    { h: 168, tempC: 6.5, precipMmH: 0.04 },
  ],
  priceOverlays: [],
  events: [{ id: 'E28', atH: 0, durationH: 168 }],
  hazardRate: 1.0,
  forecastBias: 0,
  forecastBiasDecayH: 0,
  objectives: [
    { id: 'band', text: 'Hold the forebay near 117.6 m', kind: 'hw-in-band-at-end', value: 117.6 },
    { id: 'env', text: 'Environmental compliance at least 95 %', kind: 'env-compliance-at-least', value: 95 },
  ],
  debriefHints: [
    'A steady release from upstream is worth more to you than an optimised one, and the reverse is true too — your whole operating range is 1.5 m.',
  ],
  gradeThresholds: [6_000_000, 4_500_000, 3_000_000, 1_000_000],
};

export const cascadeDownstream = downstream;
